import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Dialog,
  IconButton,
} from '@mui/material';
import { tokens } from "@/tokens";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { Dropzone } from '@/components';
import { CreateProductInput, useCreateProductsBatchMutation } from '@/services/product-catalogue/pc-api';
import { useAppSelector } from '@/store';
import { roles } from '@/roles';
import { downloadCSVTemplate } from '../csv-template/template-generator';
import CSVColumnMapper, { ColumnMapping } from '../csv-column-mapper';
import BatchProductReview from '../batch-product-review';
import useSnackbar from '@/hooks/useSnackbar';

interface CSVBatchUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (createdProductIds?: string[]) => void;
  onTriggerBulkPolling?: (productIds: string[]) => void;
}

enum UploadStep {
  UPLOAD = 0,
  MAP = 1,
  REVIEW = 2,
}

const CSVBatchUpload: React.FC<CSVBatchUploadProps> = ({ open, onClose, onSuccess, onTriggerBulkPolling }) => {
  const [activeStep, setActiveStep] = useState<UploadStep>(UploadStep.UPLOAD);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mappedProducts, setMappedProducts] = useState<CreateProductInput[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<boolean[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { show: toast } = useSnackbar();
  const [createBatch, { isLoading: isSubmitting }] = useCreateProductsBatchMutation();

  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setActiveStep(UploadStep.UPLOAD);
      setCsvFile(null);
      setCsvData([]);
      setCsvHeaders([]);
      setMappedProducts([]);
      setSelectedProducts([]);
      setIsProcessing(false);
    }
  }, [open]);

  const handleFileUpload = useCallback((file: File) => {
    if (!file) return;

    setCsvFile(file);
    setIsProcessing(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          toast('Error parsing CSV file', results.errors[0].message, 'error');
          setIsProcessing(false);
          return;
        }

        if (results.data.length === 0) {
          toast('CSV file is empty', 'Please upload a file with data', 'error');
          setIsProcessing(false);
          return;
        }

        if (results.data.length > 1000) {
          toast('Too many products', 'Maximum 1000 products allowed per upload', 'error');
          setIsProcessing(false);
          return;
        }

        setCsvHeaders(results.meta.fields || []);
        setCsvData(results.data as any[]);
        setActiveStep(UploadStep.MAP);
        setIsProcessing(false);

        toast(`Successfully parsed ${results.data.length} products`, '', 'success');
      },
      error: (error) => {
        toast('Failed to parse CSV', error.message, 'error');
        setIsProcessing(false);
      }
    });
  }, [toast]);

  const handleMappingChange = useCallback((_mapping: ColumnMapping) => {
    // Column mapping is handled by the mapper component itself
  }, []);

  const handleValidDataChange = useCallback((validData: CreateProductInput[]) => {
    setMappedProducts(validData);
    setSelectedProducts(new Array(validData.length).fill(true));
  }, []);

  const handleNextFromMapping = () => {
    if (mappedProducts.length === 0) {
      toast('No valid products found', 'Please check your column mapping and data', 'error');
      return;
    }
    setActiveStep(UploadStep.REVIEW);
  };



  const handleProductsUpdate = (updatedProducts: CreateProductInput[]) => {
    setMappedProducts(updatedProducts);
  };

  const handleSelectedProductsChange = (selected: boolean[]) => {
    setSelectedProducts(selected);
  };

  const handleReviewSubmit = async () => {
    try {
      const selectedProductsData = mappedProducts.filter((_, index) => selectedProducts[index]);

      // Check if TD Admin with TRADEDIVE_CLIENT tenant
      const isTDAdminTCD = user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";

      // Get client ID - properly handle different user roles
      const clientId = (() => {
        if (isTDAdminTCD) {
          return selectedTenant?._id;
        }
        if (selectedClient?._id) {
          return selectedClient._id;
        }
        if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
          return user?._id;
        }
        return undefined;
      })();

      // Add client/user information to each product
      const productsWithContext = selectedProductsData.map(product => ({
        ...product,
        // Always pass clientId (client/user ObjectId)
        ...(clientId ? { clientId } : {}),
        // For TD Admin, also pass tenantId
        ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
      }));

      const result = await createBatch({
        products: productsWithContext
      }).unwrap();

      const createdProductIds = result.createdProducts.map(p => p._id);

      if (result.success) {
        toast(`Successfully created ${result.summary.totalCreated} products!`, '', 'success');
        onSuccess(createdProductIds);
      } else {
        const message = `Created ${result.summary.totalCreated} products, ${result.summary.totalFailed} failed`;
        toast(message, '', result.summary.totalFailed > 0 ? 'warning' : 'success');

        if (result.errors.length > 0) {
          console.error('Batch creation errors:', result.errors);
        }

        onSuccess(createdProductIds);
      }

      // Trigger bulk polling for created products
      if (onTriggerBulkPolling && createdProductIds.length > 0) {
        onTriggerBulkPolling(createdProductIds);
      }
    } catch (error) {
      console.error('Batch upload error:', error);
      toast('Failed to upload products. Please try again.', '', 'error');
    }
  };

  // Step-based modal configurations
  const getModalTitle = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return 'Upload Product CSV';
      case UploadStep.MAP:
        return 'Map CSV Columns';
      case UploadStep.REVIEW:
        return 'Review & Submit Products';
      default:
        return 'Bulk Upload Products';
    }
  };

  const getModalSubtitle = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return 'Upload a CSV file with your product data. Maximum 1000 products per upload.';
      case UploadStep.MAP:
        return 'Map your CSV columns to product fields. Required fields are marked with *.';
      case UploadStep.REVIEW:
        return 'Review your products and fix any validation issues before submitting.';
      default:
        return '';
    }
  };

  const getButtonText = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return 'Next: Map Columns';
      case UploadStep.MAP:
        return `Review Products (${mappedProducts.length})`;
      case UploadStep.REVIEW:
        return `Upload ${mappedProducts.length} Products`;
      default:
        return 'Next';
    }
  };

  const handlePrimaryAction = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        // For upload step, the button should be disabled until file is uploaded
        // File upload automatically proceeds to next step
        break;
      case UploadStep.MAP:
        handleNextFromMapping();
        break;
      case UploadStep.REVIEW:
        handleReviewSubmit();
        break;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return csvData.length > 0;
      case UploadStep.MAP:
        return mappedProducts.length > 0;
      case UploadStep.REVIEW:
        const selectedCount = selectedProducts.filter(Boolean).length;
        const hasErrors = mappedProducts.some(product => {
          // Basic validation - check required fields
          return !product.sku || !product.name || !product.htsCode || !product.countryOfOrigin;
        });
        return selectedCount > 0 && !hasErrors && !isSubmitting;
      default:
        return false;
    }
  };

  const renderUploadStep = () => (
    <Box sx={{ bgcolor: tokens.bg.muted, p: 3 }}>
      <Box sx={{
        bgcolor: tokens.bg.surface,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: tokens.radius.lg,
        p: 3,
        textAlign: 'center',
      }}>
        <CloudUploadIcon sx={{ fontSize: 32, color: tokens.brand.primary, mb: 1.5 }} />
        <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, mb: 2 }}>
          Upload a CSV file with your product data. Maximum 1000 products per upload.
        </Typography>

        <Box sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
          <Dropzone
            onUpload={handleFileUpload}
            accept={['text/csv', 'application/vnd.ms-excel']}
            extensions={['csv']}
            fileFormat="CSV"
            maxSize={5 * 1024 * 1024} // 5MB
            disabled={isProcessing}
          />
        </Box>

        {isProcessing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing CSV file...
            </Typography>
          </Box>
        )}

        {/* Template Downloads */}
        <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${tokens.border.light}` }}>
          <Typography sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base, color: tokens.text.primary, mb: 1.5 }}>
            Need a Template?
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, mb: 2 }}>
            Download our CSV template to get started with the correct format.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => downloadCSVTemplate(true)}
              sx={{
                borderColor: tokens.border.default,
                color: tokens.text.body,
                borderRadius: tokens.radius.md,
                height: 34,
                fontSize: tokens.fontSize.md,
                boxShadow: "none",
                '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
              }}
            >
              Download with Examples
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
              onClick={() => downloadCSVTemplate(false)}
              sx={{
                borderColor: tokens.border.default,
                color: tokens.text.body,
                borderRadius: tokens.radius.md,
                height: 34,
                fontSize: tokens.fontSize.md,
                boxShadow: "none",
                '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
              }}
            >
              Download Empty Template
            </Button>
          </Stack>

          <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 2, lineHeight: 1.6 }}>
            <strong>Required:</strong> Product SKU, Product Name, HTS Code, Country of Origin. <strong>Optional:</strong> Description, Metal %. Use 2-letter country codes (US, CN, MX) and format HTS codes as XXXX.XX.XXXX
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderMappingStep = () => (
    <Box sx={{ bgcolor: tokens.bg.muted, p: 3 }}>
      <Box sx={{
        bgcolor: tokens.bg.surface,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: tokens.radius.lg,
        p: 3,
        mb: 3,
      }}>
        <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, mb: 1 }}>
          Map your CSV columns to product fields. We've auto-detected some mappings for you.
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted }}>
          <strong>File:</strong> {csvFile?.name} ({csvData.length} rows)
        </Typography>
      </Box>

      <CSVColumnMapper
        csvHeaders={csvHeaders}
        csvData={csvData}
        onMappingChange={handleMappingChange}
        onValidDataChange={handleValidDataChange}
      />
    </Box>
  );

  const renderReviewStep = () => (
    <Box sx={{ bgcolor: tokens.bg.muted, p: 3 }}>
      <BatchProductReview
        products={mappedProducts}
        onUpdate={handleProductsUpdate}
        selectedProducts={selectedProducts}
        onSelectedProductsChange={handleSelectedProductsChange}
      />
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return renderUploadStep();
      case UploadStep.MAP:
        return renderMappingStep();
      case UploadStep.REVIEW:
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "88vh",
          overflow: "hidden",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        flexShrink: 0,
      }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: "15px", color: tokens.text.primary }}>
              {getModalTitle()}
            </Typography>
            <Box sx={{
              px: 1.5,
              py: 0.25,
              borderRadius: tokens.radius.full,
              bgcolor: tokens.bg.muted,
              border: `1px solid ${tokens.border.default}`,
            }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontWeight: tokens.fontWeight.semibold }}>
                Step {activeStep + 1} of 3
              </Typography>
            </Box>
          </Stack>
          <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
            {getModalSubtitle()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: tokens.text.secondary }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {renderStepContent()}
      </Box>

      {/* Footer */}
      <Box sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1.5,
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
        flexShrink: 0,
      }}>
        {activeStep > UploadStep.UPLOAD && (
          <Button
            onClick={() => setActiveStep(prev => prev - 1)}
            variant="outlined"
            sx={{
              borderColor: tokens.border.default,
              color: tokens.text.body,
              borderRadius: tokens.radius.md,
              height: 34,
              fontSize: tokens.fontSize.md,
              boxShadow: "none",
              mr: "auto",
              '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
            }}
          >
            Back
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            height: 34,
            fontSize: tokens.fontSize.md,
            boxShadow: "none",
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePrimaryAction}
          disabled={!canProceed()}
          variant="contained"
          sx={{
            bgcolor: tokens.brand.primary,
            color: "#fff",
            borderRadius: tokens.radius.md,
            height: 34,
            fontSize: tokens.fontSize.md,
            boxShadow: "none",
            '&:hover': { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
            '&.Mui-disabled': { bgcolor: tokens.text.disabled, color: "#fff" },
          }}
        >
          {getButtonText()}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CSVBatchUpload;
